import type { FloorGeometry } from './types'

export type DemoApPosition = { x: number; y: number; txPowerDbm: number }

export type DemoFloorConfig = {
  id: string
  name: string
  widthM: number
  heightM: number
  backgroundImage: string
  geometry: FloorGeometry
  apPositions: DemoApPosition[]
}

export const DEMO_FLOOR_OFFICE_ID = 'f0000000-0000-0000-0000-000000000002'
export const DEMO_FLOOR_OFFICE_BG = '/floor-plans/building-a-level-2-v2.png'

const OFFICE_GEOMETRY: FloorGeometry = {
  version: 1,
  heatmapLayer: 'rssi',
  bandGHz: 5,
  layers: {
    layout: true,
    rooms: false,
    walls: false,
    devices: true,
    clients: true,
    heatmap: true,
    alarms: true,
    cameras: true,
    sensors: true,
    topology: false,
  },
  rooms: [
    { id: 'mr1', name: 'Meeting Room 1', x: 0, y: 0, w: 16.67, h: 25 },
    { id: 'mr2', name: 'Meeting Room 2', x: 16.67, y: 0, w: 16.67, h: 25 },
    { id: 'conf', name: 'Conference Room', x: 33.33, y: 0, w: 33.33, h: 25 },
    { id: 'mgr', name: 'Manager Room', x: 66.67, y: 0, w: 16.67, h: 25 },
    { id: 'dir', name: 'Director Room', x: 83.33, y: 0, w: 16.67, h: 25 },
    { id: 'oo1', name: 'Open Office 1', x: 0, y: 25, w: 33.33, h: 50 },
    { id: 'oo2', name: 'Open Office 2', x: 50, y: 25, w: 33.33, h: 50 },
    { id: 'ter', name: 'Terrace', x: 83.33, y: 25, w: 16.67, h: 50 },
    { id: 'pan', name: 'Pantry', x: 0, y: 75, w: 16.67, h: 25 },
    { id: 'brk', name: 'Breakout Room', x: 16.67, y: 75, w: 16.67, h: 25 },
    { id: 'it', name: 'IT Room', x: 33.33, y: 78, w: 11.11, h: 22 },
    { id: 'stor', name: 'Storage', x: 44.44, y: 78, w: 11.11, h: 22 },
    { id: 'train', name: 'Training Room', x: 55.56, y: 75, w: 16.67, h: 25 },
  ],
  walls: [
    { id: 'w-ext-l', x1: 0, y1: 0, x2: 0, y2: 100, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-ext-r', x1: 100, y1: 0, x2: 100, y2: 100, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-ext-t', x1: 0, y1: 0, x2: 100, y2: 0, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-ext-b', x1: 0, y1: 100, x2: 100, y2: 100, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-row-top', x1: 0, y1: 25, x2: 100, y2: 25, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-row-mid', x1: 33.33, y1: 25, x2: 33.33, y2: 75, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-oo2-ter', x1: 83.33, y1: 25, x2: 83.33, y2: 75, material: 'glass', heightM: 2.7, thicknessM: 0.12 },
    { id: 'w-oo2-core', x1: 50, y1: 25, x2: 50, y2: 75, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-corridor', x1: 33.33, y1: 75, x2: 72, y2: 75, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-conf-glass', x1: 33.33, y1: 25, x2: 66.67, y2: 25, material: 'glass', heightM: 2.7, thicknessM: 0.12 },
    { id: 'w-it-l', x1: 33.33, y1: 78, x2: 33.33, y2: 100, material: 'brick', heightM: 2.7, thicknessM: 0.2 },
    { id: 'w-it-r', x1: 44.44, y1: 78, x2: 44.44, y2: 100, material: 'brick', heightM: 2.7, thicknessM: 0.2 },
  ],
  obstacles: [
    { id: 'obs-elev', type: 'elevator', x: 44, y: 42, w: 7, h: 8, attenuationDb: 28 },
    { id: 'obs-stair-w', type: 'metal_cabinet', x: 36, y: 36, w: 5, h: 6, attenuationDb: 15 },
    { id: 'obs-stair-e', type: 'metal_cabinet', x: 52, y: 36, w: 5, h: 6, attenuationDb: 15 },
    { id: 'obs-rack1', type: 'server_rack', x: 35, y: 88, w: 4, h: 4, attenuationDb: 18 },
    { id: 'obs-rack2', type: 'server_rack', x: 40, y: 88, w: 4, h: 4, attenuationDb: 18 },
    { id: 'obs-elec-w', type: 'metal_cabinet', x: 38, y: 52, w: 4, h: 4, attenuationDb: 12 },
    { id: 'obs-elec-e', type: 'metal_cabinet', x: 48, y: 52, w: 4, h: 4, attenuationDb: 12 },
  ],
  devices: [
    { id: 'dev-sw-idf', type: 'switch', name: 'IDF-SW-01', x: 38, y: 90, status: 'healthy', heightM: 1.8, mount: 'rack' },
    { id: 'dev-fw', type: 'firewall', name: 'IDF-FW-01', x: 41, y: 90, status: 'healthy', heightM: 1.8, mount: 'rack' },
    { id: 'dev-wlc', type: 'wireless_controller', name: 'WLC-01', x: 38, y: 86, status: 'healthy', heightM: 1.8, mount: 'rack' },
    { id: 'dev-cam-pan', type: 'camera', name: 'Pantry-Cam-01', x: 8, y: 82, status: 'healthy', heightM: 2.8, mount: 'wall' },
    { id: 'dev-cam-conf', type: 'camera', name: 'Conf-Cam-01', x: 50, y: 10, status: 'healthy', heightM: 2.8, mount: 'ceiling' },
    { id: 'dev-sensor', type: 'sensor', name: 'Env-Sensor-01', x: 63, y: 78, status: 'healthy', heightM: 2.4, mount: 'wall' },
    { id: 'dev-ups', type: 'ups', name: 'IT-UPS-01', x: 35, y: 94, status: 'healthy', heightM: 0.5, mount: 'floor' },
  ],
}

export const DEMO_FLOOR_OFFICE: DemoFloorConfig = {
  id: DEMO_FLOOR_OFFICE_ID,
  name: 'Office — Building A Level 2',
  widthM: 36,
  heightM: 24,
  backgroundImage: DEMO_FLOOR_OFFICE_BG,
  geometry: OFFICE_GEOMETRY,
  apPositions: [
    { x: 17, y: 48, txPowerDbm: 18 },
    { x: 67, y: 48, txPowerDbm: 18 },
    { x: 50, y: 12, txPowerDbm: 16 },
    { x: 63, y: 82, txPowerDbm: 17 },
    { x: 25, y: 80, txPowerDbm: 15 },
  ],
}

export function getDemoFloorConfig(floorId: string, bgRaw: string): DemoFloorConfig | null {
  if (floorId === DEMO_FLOOR_OFFICE_ID || bgRaw.includes('building-a-level-2-v2')) {
    return DEMO_FLOOR_OFFICE
  }
  return null
}

export function isDemoFloorStale(
  floorId: string,
  bgRaw: string,
  fp: { widthM?: unknown; heightM?: unknown; floorName?: unknown },
  geo: FloorGeometry,
  config: DemoFloorConfig,
): boolean {
  if (floorId !== config.id && !bgRaw.includes(config.backgroundImage.replace(/^\//, ''))) return false
  const sparseGeometry = geo.walls.length < 8 || geo.devices.length < 4
  return sparseGeometry
}
